import type { MediaUploadResult } from "@blog/content-schema";
import {
  Editor as TipTapEditor,
  htmlToDocument,
  normalizeDocument,
  toAltText,
  type EditorHandle,
  type ProseMirrorDocument,
  serializeDocument,
} from "@blog/editor";
import { renderMarkdown } from "@blog/markdown";
import { Badge, Button, Field, Input, Textarea, cn, statusVariant } from "@blog/ui";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useApi } from "../app/ApiContext";
import { describeError, isApiError } from "../lib/api-error";
import {
  extractTitleFromHtml,
  parseDocx,
  replaceDataImages,
  stripLeadingTitleHeading,
} from "../lib/docx-import";
import { t } from "../lib/i18n";
import { useAsync, useSite } from "../lib/hooks";
import { deriveSlugFromTitle, fallbackSlug } from "../lib/slug";

/**
 * The article editor, in the shape of a writing app (TASK-803, SPEC §40).
 *
 * The layout follows Gridea's: a quiet page with the title across the top, the
 * document underneath, and a rail of small icons on the right for everything
 * that is not writing — uploads, settings, preview. The metadata lives in a
 * drawer instead of a permanent sidebar, so the first screen is prose.
 *
 * Three behaviours carry the page:
 *
 *   - **Autosave** (AGENTS.md §45): changes debounce into a draft save, with
 *     saving / saved / failed states in the header. A draft save never creates
 *     a commit (AGENTS.md §16) — only an explicit publish does.
 *   - **Lazy creation**: `/articles/new` opens the editor before any entry
 *     exists. The entry, localization and first draft are created by the first
 *     save that has something to save, and the URL is then replaced with the
 *     canonical one.
 *   - **Uploads are one function** (AGENTS.md §13): pasting, dropping and the
 *     rail's pickers all funnel through the same media call; the editor only
 *     ever sees a URL back.
 */
const AUTOSAVE_DELAY_MS = 1000;

type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

export function ArticleEditor() {
  const client = useApi();
  const params = useParams<{ entryId?: string; locale?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { site, loading: siteLoading } = useSite(client);

  const routeEntryId = params.entryId;
  const isNew = routeEntryId === undefined;
  const siteId = site?.id;
  const [locale, setLocale] = useState(params.locale ?? site?.defaultLocale ?? "zh-CN");

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tagText, setTagText] = useState("");
  const [cover, setCover] = useState("");

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [uploadingCount, setUploadingCount] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  /** Which AI command is running, so each button shows its own spinner. */
  const [aiBusy, setAiBusy] = useState<null | "slug" | "tags" | "category" | "description">(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoCharacters, setInfoCharacters] = useState(0);

  const editorHandle = useRef<EditorHandle | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const wordInputRef = useRef<HTMLInputElement | null>(null);

  /** The document the editor currently holds; changes on every keystroke. */
  const documentRef = useRef<ProseMirrorDocument | null>(null);

  /**
   * The body as it was loaded from the server (and as last saved).
   *
   * A save where the author only touched the title or the settings must keep
   * this body: `documentRef` is still `null` until the first keystroke in the
   * editor, and serializing it would write an empty article over the draft —
   * which is exactly how the RAG draft lost its body on 2026-10-13.
   */
  const savedContentRef = useRef("");

  /**
   * True once the user has changed anything since the last successful save.
   *
   * Publishing or previewing with no pending edits must not re-save the form:
   * `documentRef` is `null` until the first edit, and serializing it would wipe
   * a draft that exists only on the server. A clean form simply publishes the
   * draft as it is.
   */
  const dirtyRef = useRef(false);

  /** False until the form is seeded, so loading never triggers an autosave. */
  const readyRef = useRef(false);
  /** True once the user edits the slug by hand; title then stops deriving it. */
  const slugTouchedRef = useRef(false);
  const autosaveTimerRef = useRef<number | null>(null);
  const creatingRef = useRef<Promise<{ id: string; created: boolean }> | null>(null);
  const createdIdRef = useRef<string | null>(isNew ? null : (routeEntryId ?? null));

  /**
   * The revision this draft was loaded against (SPEC §25). Sent with every save
   * so a publish can tell "still editing" from "the repository moved".
   */
  const baseShaRef = useRef<string | undefined>(undefined);

  /** Set when a publish reports `CONTENT_CONFLICT`: the repository's revision. */
  const [conflictRevision, setConflictRevision] = useState<string | null>(null);

  /**
   * The editor's starting content.
   *
   * A draft is stored as Markdown; it goes through the shared renderer and then
   * the editor's own document model (`htmlToDocument`), which also normalizes
   * it — escape residue and Word's underlined subheadings are healed on open,
   * and the next autosave stores the healed form. A brand-new article has
   * nothing to load and starts empty at once.
   */
  const [initialContent, setInitialContent] = useState<ProseMirrorDocument | string | null>(
    isNew ? "" : null,
  );

  const categories = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return await client.listTaxonomy(siteId, "categories");
  }, [siteId]);

  const loaded = useAsync(async () => {
    if (siteId === undefined || routeEntryId === undefined) {
      return null;
    }

    const [localization, draft] = await Promise.all([
      client.getLocalization(siteId, routeEntryId, locale),
      client.getDraft(siteId, routeEntryId, locale),
    ]);

    return { localization, draft };
  }, [siteId, routeEntryId, locale]);

  /**
   * The publish state the server actually holds.
   *
   * Only the 发布 button and the unpublish flow change it; the draft's own
   * frontmatter status is metadata, not fact — showing it in the header once
   * advertised articles as "published" that had never left the building.
   */
  const localization = loaded.data?.localization ?? null;
  const isPublished = localization?.status === "published";

  // Seed the form once the draft arrives. The editor is uncontrolled after
  // this, so re-seeding on every render would fight the user's typing.
  useEffect(() => {
    const data = loaded.data;

    if (data === null) {
      // A new article has nothing to load — start accepting autosaves at once.
      if (isNew) {
        readyRef.current = true;
      }

      return;
    }

    setTitle(data.draft?.frontmatter.title ?? data.localization.title);
    setSlug(data.draft?.frontmatter.slug ?? data.localization.slug);
    setDescription(data.draft?.frontmatter.description ?? data.localization.description ?? "");
    setCategory(data.draft?.frontmatter.category ?? "");
    setTagText((data.draft?.frontmatter.tags ?? []).join(", "));
    setCover(data.draft?.frontmatter.cover ?? "");

    // An empty draft keeps the HTML string path; a real one becomes a
    // normalized document, so the editor never opens on escape residue.
    // `autolink: false`: heading anchors are site presentation — rendering
    // them here would grow a stray `#` at the end of every heading.
    const markdown = data.draft?.content ?? "";
    savedContentRef.current = markdown;
    setInitialContent(
      markdown.trim() === ""
        ? ""
        : htmlToDocument(renderMarkdown(markdown, { autolink: false })),
    );

    baseShaRef.current = data.draft?.baseGithubSha;
    // The server's draft is now exactly what the form shows: nothing pending.
    dirtyRef.current = false;
    readyRef.current = true;
  }, [loaded.data, isNew]);

  // A one-off notice handed over by the page that created this entry (the Word
  // import reports images it could not transfer). Shown once, then cleared so a
  // reload does not repeat it. Runs once per mount; the point is exactly the
  // state at hand-off.
  const notice = (location.state as { readonly notice?: string } | null)?.notice;
  useEffect(() => {
    if (typeof notice === "string" && notice !== "") {
      setMessage(notice);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  /**
   * The slug a save or a publish will use.
   *
   * A title the user has only typed — Chinese above all — can derive to an
   * empty slug, so a timestamp takes over rather than emitting nothing.
   */
  function currentSlug(): string {
    if (slug !== "") {
      return slug;
    }

    return deriveSlugFromTitle(title, fallbackSlug(new Date()));
  }

  /**
   * Create the entry and its first localization, once, on demand.
   *
   * Everything the editor needs later — an id for drafts, for media, for
   * publishing — hangs off this. In-flight creations are shared so a paste and
   * an autosave landing together cannot create two entries.
   */
  async function ensureEntry(): Promise<{ id: string; created: boolean }> {
    if (createdIdRef.current !== null) {
      return { id: createdIdRef.current, created: false };
    }

    if (siteId === undefined) {
      throw new Error("No site");
    }

    if (creatingRef.current !== null) {
      return await creatingRef.current;
    }

    creatingRef.current = (async () => {
      const finalSlug = currentSlug();
      const entry = await client.createEntry(siteId, {
        type: "post",
        canonicalLocale: locale,
        contentPath: `posts/${finalSlug}`,
      });

      // A localization has to exist before a draft can (the draft route
      // checks for one), so creation is a two-step, still commit-free.
      await client.saveLocalization(
        siteId,
        entry.id,
        locale,
        {
          title: title.trim() === "" ? t("common.untitled") : title.trim(),
          slug: finalSlug,
          status: "draft",
          contentPath: `posts/${finalSlug}/${locale}.md`,
        },
        { create: true },
      );

      createdIdRef.current = entry.id;

      return { id: entry.id, created: true };
    })();

    try {
      return await creatingRef.current;
    } catch (cause) {
      // Allow a retry after a failed creation (a slug collision, say).
      creatingRef.current = null;
      throw cause;
    }
  }

  /**
   * Persist the current form as a draft.
   *
   * Returns the entry it saved under, so publish and preview can act on the
   * document that is actually on the server rather than the one in the URL.
   */
  async function persist(): Promise<{ ok: boolean; id: string | null }> {
    if (siteId === undefined) {
      return { ok: false, id: null };
    }

    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    const entryId = createdIdRef.current;

    // Nothing pending: the saved draft on the server is already the truth, and
    // serializing an untouched editor would wipe it. A brand-new article with
    // no edits has neither, so there is nothing to do at all.
    if (!dirtyRef.current) {
      return entryId === null ? { ok: false, id: null } : { ok: true, id: entryId };
    }

    // A body that was never typed into is the body already on the server —
    // serializing the null document would wipe it (see savedContentRef).
    // normalizeDocument before serializing: paste-borne escape residue stops
    // at the save boundary even if the view correction was missed.
    const markdown =
      documentRef.current === null
        ? savedContentRef.current
        : serializeDocument(normalizeDocument(documentRef.current));

    // A visit alone creates nothing: an empty editor on /articles/new stays
    // three HTTP requests short of a row in D1.
    if (entryId === null && title.trim() === "" && markdown.trim() === "") {
      return { ok: false, id: null };
    }

    setSaveState("saving");

    try {
      const { id, created } = await ensureEntry();

      const tags = tagText
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag !== "");

      await client.saveDraft(siteId, id, locale, {
        content: markdown,
        frontmatter: {
          title: title.trim() === "" ? t("common.untitled") : title.trim(),
          slug: currentSlug(),
          ...(description === "" ? {} : { description }),
          ...(category.trim() === "" ? {} : { category: category.trim() }),
          ...(tags.length === 0 ? {} : { tags }),
          ...(cover.trim() === "" ? {} : { cover: cover.trim() }),
          // Mirrors the server's publish state, which only 发布/下线 change —
          // the author cannot type a "published" that never happened.
          status: localization?.status ?? "draft",
        },
        ...(baseShaRef.current === undefined ? {} : { baseGithubSha: baseShaRef.current }),
      });

      dirtyRef.current = false;
      savedContentRef.current = markdown;
      setSavedAt(new Date().toISOString());
      setSaveState("saved");
      setFieldErrors({});

      // The first save gives the article its canonical URL; the reload also
      // means the draft that was just written is what the page shows.
      if (created) {
        navigate(`/articles/${id}/${locale}`, { replace: true });
      }

      return { ok: true, id };
    } catch (cause) {
      setError(describeError(cause));

      if (isApiError(cause)) {
        setFieldErrors(cause.fieldErrors);
      }

      setSaveState("error");

      return { ok: false, id: null };
    }
  }

  // Kept in a ref so the debounced autosave always calls the current persist,
  // with the current form, rather than the closure from its first render.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const touch = useCallback(() => {
    if (!readyRef.current || siteId === undefined) {
      return;
    }

    dirtyRef.current = true;
    setSaveState((prev) => (prev === "pending" || prev === "saving" ? prev : "pending"));

    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      void persistRef.current();
    }, AUTOSAVE_DELAY_MS);
  }, [siteId]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  const onDocumentChange = useCallback(
    (document: ProseMirrorDocument) => {
      documentRef.current = document;
      touch();
    },
    [touch],
  );

  /** Upload through the Worker, which puts the bytes in Git (AGENTS.md §13). */
  async function uploadMediaFile(file: File): Promise<MediaUploadResult | null> {
    if (siteId === undefined) {
      return null;
    }

    setUploadingCount((count) => count + 1);
    setUploadError(null);

    try {
      const { id } = await ensureEntry();
      return await client.uploadMedia(siteId, id, file);
    } catch (cause) {
      setUploadError(describeError(cause));
      return null;
    } finally {
      setUploadingCount((count) => count - 1);
    }
  }

  // Plain functions, recreated per render: the editor holds these callbacks in
  // refs and always invokes the latest, so identity churn is harmless.
  async function uploadImage(file: File): Promise<string | null> {
    const result = await uploadMediaFile(file);

    return result?.url ?? null;
  }

  async function uploadAttachment(
    file: File,
  ): Promise<{ url: string; filename: string } | null> {
    const result = await uploadMediaFile(file);

    return result === null ? null : { url: result.url, filename: result.filename };
  }

  async function onImageFilesChosen(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";

    for (const file of files) {
      const result = await uploadMediaFile(file);

      if (result !== null) {
        editorHandle.current?.insertImage({
          src: result.url,
          alt: toAltText(file.name) || "image",
        });
      }
    }
  }

  async function onAttachmentFilesChosen(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";

    for (const file of files) {
      const result = await uploadMediaFile(file);

      if (result !== null) {
        editorHandle.current?.insertLink({ href: result.url, text: result.filename });
      }
    }
  }

  /**
   * Import a Word document over the article being edited.
   *
   * Title and content are replaced; slug, locale and metadata are not — a
   * published article keeps its URL. Embedded images upload into this entry's
   * media (AGENTS.md §13); the document itself is never stored. `replaceContent`
   * does not fire `onChange`, so the document reference and the autosave are
   * updated here.
   */
  async function onImportFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (file === undefined || siteId === undefined) {
      return;
    }

    if (!/\.docx$/i.test(file.name)) {
      setImportError(t("articles.importNotDocx"));
      return;
    }

    if (!window.confirm(t("editor.importConfirm"))) {
      return;
    }

    setImporting(true);
    setImportError(null);

    try {
      const bytes = await file.arrayBuffer();
      const { html } = await parseDocx(bytes);

      // The first heading becomes the title; the body keeps the rest.
      const title = extractTitleFromHtml(html) ?? file.name.replace(/\.docx$/i, "");
      const bodyHtml = stripLeadingTitleHeading(html, title);
      const { html: withImages, failed } = await replaceDataImages(bodyHtml, async (image) => {
        const result = await uploadMediaFile(image);
        return result?.url ?? null;
      });

      const document = htmlToDocument(withImages);
      editorHandle.current?.replaceContent(document);
      documentRef.current = document;
      setTitle(title);
      touch();

      if (failed > 0) {
        setImportError(t("articles.importImagesFailed", { count: failed }));
      }
    } catch (cause) {
      setImportError(describeError(cause));
    } finally {
      setImporting(false);
    }
  }

  /**
   * AI assistance over the site's default model (SPEC §74).
   *
   * Each command is a plain API call — the Worker resolves provider, model and
   * key, and returns a server-validated answer. Results land in the same
   * fields a manual edit would, so they save (and publish) through the normal
   * path and the user can adjust them afterwards.
   */
  async function generateSlug(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    if (title.trim() === "") {
      setAiError(t("editor.aiNeedsTitle"));
      return;
    }

    setAiBusy("slug");
    setAiError(null);
    setAiNotice(null);

    try {
      const result = await client.generateSlug(siteId, { title: title.trim(), locale });

      slugTouchedRef.current = true;
      setSlug(result.slug);
      touch();
    } catch (cause) {
      setAiError(describeError(cause));
    } finally {
      setAiBusy(null);
    }
  }

  async function generateTags(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    if (title.trim() === "") {
      setAiError(t("editor.aiNeedsTitle"));
      return;
    }

    setAiBusy("tags");
    setAiError(null);
    setAiNotice(null);

    try {
      const markdown =
        documentRef.current === null ? "" : serializeDocument(documentRef.current);

      const result = await client.generateTags(siteId, {
        title: title.trim(),
        // The model names an article from its head; the API caps the rest.
        ...(markdown === "" ? {} : { content: markdown.slice(0, 100_000) }),
      });

      setTagText(result.tags.join(", "));
      touch();
    } catch (cause) {
      setAiError(describeError(cause));
    } finally {
      setAiBusy(null);
    }
  }

  async function generateCategory(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    if (title.trim() === "") {
      setAiError(t("editor.aiNeedsTitle"));
      return;
    }

    setAiBusy("category");
    setAiError(null);
    setAiNotice(null);

    try {
      const markdown =
        documentRef.current === null ? "" : serializeDocument(documentRef.current);

      const result = await client.generateCategory(siteId, {
        title: title.trim(),
        ...(markdown === "" ? {} : { content: markdown.slice(0, 100_000) }),
      });

      setCategory(result.category);
      touch();

      if (result.isNew) {
        setAiNotice(t("editor.aiCategoryNew", { name: result.name }));
      }
    } catch (cause) {
      setAiError(describeError(cause));
    } finally {
      setAiBusy(null);
    }
  }

  async function generateDescription(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    if (title.trim() === "") {
      setAiError(t("editor.aiNeedsTitle"));
      return;
    }

    setAiBusy("description");
    setAiError(null);
    setAiNotice(null);

    try {
      const markdown =
        documentRef.current === null ? "" : serializeDocument(documentRef.current);

      const result = await client.generateDescription(siteId, {
        title: title.trim(),
        ...(markdown === "" ? {} : { content: markdown.slice(0, 100_000) }),
      });

      setDescription(result.description);
      touch();
    } catch (cause) {
      setAiError(describeError(cause));
    } finally {
      setAiBusy(null);
    }
  }

  async function publish(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    setBusy(true);
    setMessage(null);
    setError(null);
    setConflictRevision(null);

    try {
      // Publish reads the draft, so an unsaved change must land first.
      const saved = await persist();

      if (!saved.ok || saved.id === null) {
        return;
      }

      const result = await client.publish(siteId, saved.id, locale);
      baseShaRef.current = result.revision;
      setMessage(t("editor.publishedAs", { sha: result.revision.slice(0, 7) }));
      loaded.reload();
    } catch (cause) {
      setError(describeError(cause));

      // The repository moved (SPEC §25). The remedy is explicit: the author
      // has seen the conflict and chooses to publish this version anyway.
      if (isApiError(cause) && cause.code === "CONTENT_CONFLICT") {
        const currentRevision = cause.details?.currentRevision;

        if (typeof currentRevision === "string") {
          setConflictRevision(currentRevision);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  /** Publish over an external change, after the author has seen it. */
  async function publishAnyway(): Promise<void> {
    if (siteId === undefined || routeEntryId === undefined || conflictRevision === null) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const saved = await persist();

      if (!saved.ok || saved.id === null) {
        return;
      }

      const result = await client.publish(siteId, saved.id, locale, {
        baseGithubSha: conflictRevision,
      });

      baseShaRef.current = result.revision;
      setConflictRevision(null);
      setMessage(t("editor.publishedOver", { sha: result.revision.slice(0, 7) }));
      loaded.reload();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function unpublish(): Promise<void> {
    if (siteId === undefined || routeEntryId === undefined) {
      return;
    }

    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      await client.unpublish(siteId, routeEntryId, locale);
      setMessage(t("editor.unpublished"));
      loaded.reload();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Render the current draft exactly as the site would (TASK-403, SPEC §23).
   *
   * The same renderer runs in the editor's initial load, the preview and the
   * Astro build, so a preview that looks right is right.
   */
  async function openPreview(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      const saved = await persist();

      if (!saved.ok || saved.id === null) {
        return;
      }

      const rendered = await client.preview(siteId, saved.id, locale);
      setPreviewHtml(rendered.html);
      setPreviewOpen(true);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  function toggleDrawer(): void {
    setDrawerOpen((open) => !open);
    setInfoOpen(false);
  }

  /**
   * The header's save button: what autosave does, on request.
   *
   * A clean article still deserves an answer — "already saved" — rather than
   * a click that visibly does nothing.
   */
  function saveNow(): void {
    if (!dirtyRef.current) {
      setSavedAt(new Date().toISOString());
      setSaveState("saved");
      return;
    }

    void persist();
  }

  function toggleInfo(): void {
    const next = !infoOpen;

    setInfoOpen(next);

    if (next) {
      setInfoCharacters(editorHandle.current?.characters() ?? 0);
      setDrawerOpen(false);
    }
  }

  // Switching locale reloads the page, so the labels are stable for a mount.
  // Kept with the other hooks — above any early return (Rules of Hooks).
  const toolbarLabels = useMemo(
    () => ({
      undo: t("editor.tbUndo"),
      redo: t("editor.tbRedo"),
      paragraph: t("editor.tbParagraph"),
      heading1: t("editor.tbHeading1"),
      heading2: t("editor.tbHeading2"),
      heading3: t("editor.tbHeading3"),
      heading4: t("editor.tbHeading4"),
      bold: t("editor.tbBold"),
      italic: t("editor.tbItalic"),
      underline: t("editor.tbUnderline"),
      strike: t("editor.tbStrike"),
      code: t("editor.tbCode"),
      clearFormat: t("editor.tbClearFormat"),
      bulletList: t("editor.tbBulletList"),
      orderedList: t("editor.tbOrderedList"),
      taskList: t("editor.tbTaskList"),
      blockquote: t("editor.tbBlockquote"),
      codeBlock: t("editor.tbCodeBlock"),
      horizontalRule: t("editor.tbHr"),
      link: t("editor.tbLink"),
      linkPrompt: t("editor.tbLinkPrompt"),
      table: t("editor.tbTable"),
      tableRow: t("editor.tbRow"),
      tableRowDelete: t("editor.tbRowDelete"),
      tableColumn: t("editor.tbColumn"),
      tableColumnDelete: t("editor.tbColumnDelete"),
      tableHeaderRow: t("editor.tbHeaderRow"),
      tableDelete: t("editor.tbTableDelete"),
    }),
    [],
  );

  if (siteLoading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  const saveStatus = saveStateSlot(saveState, savedAt, uploadingCount, () => {
    void persist();
  });

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-neutral-200 pb-3">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/articles"
            aria-label={t("common.back")}
            title={t("common.back")}
            className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <IconArrowLeft />
          </Link>
          {saveStatus}
        </div>

        <div className="flex items-center gap-2">
          {isPublished && (
            <Badge variant={statusVariant("published")}>{localization?.status}</Badge>
          )}
          <Button
            size="sm"
            variant="ghost"
            loading={saveState === "saving"}
            disabled={busy}
            onClick={() => {
              saveNow();
            }}
          >
            <IconCheck />
            {t("editor.saveDraft")}
          </Button>
          <Button size="sm" loading={busy} onClick={() => void publish()}>
            <IconCheckCheck />
            {t("editor.publish")}
          </Button>
        </div>
      </header>

      {message === null ? null : (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {message}
        </div>
      )}
      {error === null ? null : (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
          {conflictRevision !== null && (
            <Button
              className="ml-3"
              size="sm"
              variant="outline"
              onClick={() => void publishAnyway()}
            >
              {t("editor.publishAnyway")}
            </Button>
          )}
        </div>
      )}
      {uploadError === null ? null : (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t("editor.uploadFailed")}: {uploadError}
        </div>
      )}
      {importError === null ? null : (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {t("editor.importFailed")}: {importError}
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 gap-2 pt-4">
        <div className="min-w-0 flex-1 overflow-y-auto pb-16 pr-2">
          <input
            className="w-full border-0 bg-transparent p-0 text-3xl font-semibold text-neutral-900 placeholder:text-neutral-300 focus:outline-none"
            placeholder={t("editor.titlePlaceholder")}
            aria-label={t("editor.title")}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);

              if (!slugTouchedRef.current) {
                setSlug(deriveSlugFromTitle(event.target.value, ""));
              }

              touch();
            }}
          />

          {initialContent === null ? (
            <p className="mt-6 text-sm text-neutral-500">{t("editor.loadingDraft")}</p>
          ) : (
            <TipTapEditor
              ref={editorHandle}
              initialContent={initialContent}
              placeholder={t("editor.bodyPlaceholder")}
              onChange={onDocumentChange}
              uploadImage={uploadImage}
              uploadAttachment={uploadAttachment}
              toolbarLabels={toolbarLabels}
              className="mt-4"
            />
          )}
        </div>

        {/* The rail: everything that is not writing. */}
        <aside className="flex flex-col items-center gap-1 pt-2">
          <RailButton
            label={t("editor.info")}
            active={infoOpen}
            onClick={() => {
              toggleInfo();
            }}
          >
            <IconInfo />
          </RailButton>
          <RailButton
            label={t("editor.uploadImage")}
            onClick={() => {
              imageInputRef.current?.click();
            }}
          >
            <IconImage />
          </RailButton>
          <RailButton
            label={t("editor.uploadAttachment")}
            onClick={() => {
              attachmentInputRef.current?.click();
            }}
          >
            <IconPaperclip />
          </RailButton>
          <RailButton
            label={t("editor.importWord")}
            disabled={importing}
            onClick={() => {
              wordInputRef.current?.click();
            }}
          >
            <IconFileText />
          </RailButton>
          <RailButton
            label={t("editor.settings")}
            active={drawerOpen}
            onClick={() => {
              toggleDrawer();
            }}
          >
            <IconSliders />
          </RailButton>
          <RailButton
            label={t("editor.preview")}
            active={previewOpen}
            onClick={() => {
              void openPreview();
            }}
          >
            <IconEye />
          </RailButton>
        </aside>

        {drawerOpen && (
          <SettingsDrawer
            slug={slug}
            slugError={fieldErrors.slug}
            onSlugChange={(value) => {
              slugTouchedRef.current = true;
              setSlug(value);
              touch();
            }}
            description={description}
            onDescriptionChange={(value) => {
              setDescription(value);
              touch();
            }}
            category={category}
            onCategoryChange={(value) => {
              setCategory(value);
              touch();
            }}
            categories={(categories.data ?? []).map((term) => ({ slug: term.slug, name: term.name }))}
            tagText={tagText}
            onTagTextChange={(value) => {
              setTagText(value);
              touch();
            }}
            cover={cover}
            onCoverChange={(value) => {
              setCover(value);
              touch();
            }}
            locale={locale}
            locales={site?.supportedLocales ?? [locale]}
            localeLocked={createdIdRef.current !== null || !isNew}
            onLocaleChange={setLocale}
            contentPath={localization?.contentPath ?? null}
            draftPath={`posts/${currentSlug()}`}
            canUnpublish={routeEntryId !== undefined && isPublished}
            unpublishBusy={busy}
            onUnpublish={() => {
              void unpublish();
            }}
            aiBusy={aiBusy}
            aiError={aiError}
            aiNotice={aiNotice}
            onGenerateSlug={() => {
              void generateSlug();
            }}
            onGenerateTags={() => {
              void generateTags();
            }}
            onGenerateCategory={() => {
              void generateCategory();
            }}
            onGenerateDescription={() => {
              void generateDescription();
            }}
          />
        )}

        {infoOpen && (
          <div className="absolute right-14 top-2 w-64 rounded-xl border border-neutral-200 bg-white p-4 shadow-lg">
            <p className="text-sm font-semibold text-neutral-900">{t("editor.infoTitle")}</p>
            <dl className="mt-3 flex flex-col gap-2 text-sm">
              <InfoRow label={t("editor.infoCharacters")}>
                {String(infoCharacters)}
              </InfoRow>
              <InfoRow label={t("common.status")}>
                <Badge variant={statusVariant(localization?.status ?? "draft")}>
                  {localization?.status ?? "draft"}
                </Badge>
              </InfoRow>
              <InfoRow label={t("common.locale")}>{locale}</InfoRow>
              <InfoRow label={t("editor.infoPath")}>
                <span className="break-all">{localization?.contentPath ?? `posts/${currentSlug()}`}</span>
              </InfoRow>
            </dl>
          </div>
        )}
      </div>

      {/* Hidden pickers. The rail opens them; `onChange` uploads and inserts. */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(event) => {
          void onImageFilesChosen(event);
        }}
      />
      <input
        ref={attachmentInputRef}
        type="file"
        accept="application/pdf,application/zip,text/plain,text/csv,audio/mpeg,video/mp4"
        multiple
        className="hidden"
        onChange={(event) => {
          void onAttachmentFilesChosen(event);
        }}
      />
      <input
        ref={wordInputRef}
        type="file"
        accept=".docx"
        className="hidden"
        onChange={(event) => {
          void onImportFile(event);
        }}
      />

      {previewOpen && previewHtml !== null && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
          <div className="sticky top-0 flex items-center justify-between border-b border-neutral-200 bg-white/90 px-6 py-3 backdrop-blur">
            <p className="text-sm font-medium text-neutral-700">{t("editor.previewHeading")}</p>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t("editor.closePreview")}
              onClick={() => {
                setPreviewOpen(false);
              }}
            >
              <IconClose />
            </Button>
          </div>
          <article
            className="prose prose-neutral mx-auto max-w-2xl px-6 py-10"
            /* Already sanitized by the shared renderer (AGENTS.md §12). */
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      )}
    </div>
  );
}

/** The header slot that tells the user where their text stands (AGENTS.md §45). */function saveStateSlot(
  state: SaveState,
  savedAt: string | null,
  uploadingCount: number,
  onRetry: () => void,
): ReactNode {
  if (uploadingCount > 0) {
    return (
      <span className="truncate text-xs text-neutral-500">
        {t("editor.uploading", { count: uploadingCount })}
      </span>
    );
  }

  if (state === "saving" || state === "pending") {
    return <span className="truncate text-xs text-neutral-400">{t("editor.autosaving")}</span>;
  }

  if (state === "saved" && savedAt !== null) {
    const time = new Date(savedAt).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <span className="truncate text-xs text-neutral-400">
        {t("editor.autosaved", { time })}
      </span>
    );
  }

  if (state === "error") {
    return (
      <button
        type="button"
        className="truncate text-xs text-red-600 hover:underline"
        onClick={onRetry}
      >
        {t("editor.autosaveFailed")}
      </button>
    );
  }

  return null;
}

/** One icon button on the right-hand rail. */
function RailButton({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  readonly label: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 disabled:pointer-events-none disabled:opacity-50",
        active && "bg-neutral-100 text-neutral-900",
      )}
    >
      {children}
    </button>
  );
}

interface SettingsDrawerProps {
  readonly slug: string;
  readonly slugError: string | undefined;
  readonly onSlugChange: (value: string) => void;
  readonly description: string;
  readonly onDescriptionChange: (value: string) => void;
  readonly category: string;
  readonly onCategoryChange: (value: string) => void;
  readonly categories: readonly { readonly slug: string; readonly name: string }[];
  readonly tagText: string;
  readonly onTagTextChange: (value: string) => void;
  readonly cover: string;
  readonly onCoverChange: (value: string) => void;
  readonly locale: string;
  readonly locales: readonly string[];
  readonly localeLocked: boolean;
  readonly onLocaleChange: (value: string) => void;
  readonly contentPath: string | null;
  readonly draftPath: string;
  readonly canUnpublish: boolean;
  readonly unpublishBusy: boolean;
  readonly onUnpublish: () => void;
  /** AI assistance (SPEC §74): which command runs, what it reported. */
  readonly aiBusy: null | "slug" | "tags" | "category" | "description";
  readonly aiError: string | null;
  readonly aiNotice: string | null;
  readonly onGenerateSlug: () => void;
  readonly onGenerateTags: () => void;
  readonly onGenerateCategory: () => void;
  readonly onGenerateDescription: () => void;
}

/** The metadata drawer, opened from the rail's sliders icon. */
function SettingsDrawer(props: SettingsDrawerProps) {
  return (
    <aside className="absolute bottom-0 right-14 top-0 w-80 overflow-y-auto rounded-xl border border-neutral-200 bg-white p-5 shadow-lg">
      <p className="text-sm font-semibold text-neutral-900">{t("editor.settingsTitle")}</p>

      <div className="mt-4 flex flex-col gap-4">
        <Field label={t("editor.slug")} error={props.slugError}>
          <div className="flex items-center gap-2">
            <Input
              value={props.slug}
              placeholder={t("editor.titlePlaceholder")}
              invalid={props.slugError !== undefined}
              onChange={(event) => {
                props.onSlugChange(event.target.value);
              }}
            />
            <AiButton
              label={t("editor.aiGenerate")}
              busy={props.aiBusy === "slug"}
              onClick={props.onGenerateSlug}
            />
          </div>
        </Field>

        <Field label={t("editor.description")} hint={t("editor.descriptionHint")}>
          <div className="flex items-start gap-2">
            <Textarea
              className="flex-1"
              value={props.description}
              onChange={(event) => {
                props.onDescriptionChange(event.target.value);
              }}
            />
            <AiButton
              label={t("editor.aiGenerate")}
              busy={props.aiBusy === "description"}
              onClick={props.onGenerateDescription}
            />
          </div>
        </Field>

        <Field label={t("editor.category")} hint={t("editor.categoryHint")}>
          <div className="flex items-center gap-2">
            <Input
              value={props.category}
              list="editor-category-options"
              onChange={(event) => {
                props.onCategoryChange(event.target.value);
              }}
            />
            <AiButton
              label={t("editor.aiSuggest")}
              busy={props.aiBusy === "category"}
              onClick={props.onGenerateCategory}
            />
          </div>
          <datalist id="editor-category-options">
            {props.categories.map((term) => (
              <option key={term.slug} value={term.slug}>
                {term.name}
              </option>
            ))}
          </datalist>
        </Field>

        <Field label={t("editor.tags")} hint={t("editor.tagsHint")}>
          <div className="flex items-center gap-2">
            <Input
              value={props.tagText}
              onChange={(event) => {
                props.onTagTextChange(event.target.value);
              }}
            />
            <AiButton
              label={t("editor.aiGenerate")}
              busy={props.aiBusy === "tags"}
              onClick={props.onGenerateTags}
            />
          </div>
        </Field>

        <Field label={t("editor.cover")} hint={t("editor.coverHint")}>
          <Input
            value={props.cover}
            placeholder="https://…"
            onChange={(event) => {
              props.onCoverChange(event.target.value);
            }}
          />
        {props.cover === "" ? null : (
          <img
            src={props.cover}
            alt={t("editor.cover")}
            className="mt-2 max-h-32 w-full rounded-md border border-neutral-200 object-cover"
          />
        )}
        </Field>

        <Field label={t("editor.canonicalLocale")}>
          {props.localeLocked ? (
            <Input value={props.locale} readOnly />
          ) : (
            <select
              className="h-9 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm"
              value={props.locale}
              onChange={(event) => {
                props.onLocaleChange(event.target.value);
              }}
            >
              {props.locales.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          )}
        </Field>

        <div>
          <p className="mb-1.5 text-sm font-medium text-neutral-700">{t("editor.infoPath")}</p>
          <p className="break-all text-xs text-neutral-500">
            {props.contentPath ?? props.draftPath}
          </p>
        </div>

        {props.aiNotice !== null ? (
          <p role="status" className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            {props.aiNotice}
          </p>
        ) : null}
        {props.aiError !== null ? (
          <p role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-700">
            {t("editor.aiFailed")}: {props.aiError}
          </p>
        ) : null}

        {props.canUnpublish && (
          <Button
            variant="outline"
            loading={props.unpublishBusy}
            onClick={props.onUnpublish}
          >
            {t("editor.unpublish")}
          </Button>
        )}
      </div>
    </aside>
  );
}

/** The small "AI" affordance beside a metadata field (SPEC §74). */
function AiButton({
  label,
  busy,
  onClick,
}: {
  readonly label: string;
  readonly busy: boolean;
  readonly onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="shrink-0"
      disabled={busy}
      loading={busy}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {busy ? "…" : "AI"}
    </Button>
  );
}

function InfoRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="shrink-0 text-neutral-500">{label}</dt>
      <dd className="text-right text-neutral-800">{children}</dd>
    </div>
  );
}

/** Stroke-icon wrapper; every icon on this page shares the same hand. */
function Svg({ children }: { readonly children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <Svg>
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </Svg>
  );
}

function IconCheck() {
  return (
    <Svg>
      <path d="M20 6 9 17l-5-5" />
    </Svg>
  );
}

function IconCheckCheck() {
  return (
    <Svg>
      <path d="M18 6 7 17l-5-5" />
      <path d="m22 10-7.5 7.5L13 16" />
    </Svg>
  );
}

function IconInfo() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01" />
      <path d="M12 11v5" />
    </Svg>
  );
}

function IconImage() {
  return (
    <Svg>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </Svg>
  );
}

function IconPaperclip() {
  return (
    <Svg>
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </Svg>
  );
}

function IconFileText() {
  return (
    <Svg>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </Svg>
  );
}

function IconSliders() {
  return (
    <Svg>
      <line x1="21" y1="4" x2="14" y2="4" />
      <line x1="10" y1="4" x2="3" y2="4" />
      <line x1="21" y1="12" x2="12" y2="12" />
      <line x1="8" y1="12" x2="3" y2="12" />
      <line x1="21" y1="20" x2="16" y2="20" />
      <line x1="12" y1="20" x2="3" y2="20" />
      <line x1="14" y1="2" x2="14" y2="6" />
      <line x1="8" y1="10" x2="8" y2="14" />
      <line x1="16" y1="18" x2="16" y2="22" />
    </Svg>
  );
}

function IconEye() {
  return (
    <Svg>
      <path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

function IconClose() {
  return (
    <Svg>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Svg>
  );
}
